import React, { useState } from 'react';
import { Box, TextField, Button, Link, Alert, Typography } from '@mui/material';
import { agentRegistrationService } from '../../services/agentPortalService';

/**
 * Neutral confirmation shown after EVERY resend attempt.
 *
 * The backend is enumeration-hardened: /auth/resend-verification returns one identical response
 * for unknown, already-verified and awaiting-verification addresses. This component must preserve
 * that property in the UI, so it renders this single string on success AND on network failure and
 * never branches on the response body. Do not "improve" this into per-case copy.
 */
const NEUTRAL_CONFIRMATION =
  "If that address needs verifying, we've emailed a new verification link. It can take a minute to arrive — remember to check your spam folder.";

interface ResendVerificationProps {
  /** Pre-fills the address field. Purely the user's own input — never server-derived. */
  defaultEmail?: string;
  /** Text of the disclosure link that reveals the form. */
  linkLabel?: string;
  /** Render the form immediately instead of behind a disclosure link. */
  startExpanded?: boolean;
}

/**
 * Self-service "send me another verification link" affordance.
 *
 * Exists because a lapsed 24h verification link used to be a permanent lockout: login filters on
 * IsActive so an unverified account is indistinguishable from a nonexistent one, and re-registering
 * the same address is silently suppressed for enumeration reasons.
 */
const ResendVerification: React.FC<ResendVerificationProps> = ({
  defaultEmail = '',
  linkLabel = 'Resend verification email',
  startExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(startExpanded);
  const [email, setEmail] = useState(defaultEmail);
  const [emailError, setEmailError] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setEmailError('Please enter a valid email');
      return;
    }
    setEmailError('');
    setSending(true);
    try {
      await agentRegistrationService.resendVerification(email);
    } catch {
      // Swallow deliberately: surfacing a failure here would turn a network/server condition into
      // a signal about the address. The confirmation below is unconditional.
    } finally {
      setSending(false);
      setSent(true);
    }
  };

  if (sent) {
    return (
      <Alert severity="info" sx={{ mt: 2, textAlign: 'left' }}>
        {NEUTRAL_CONFIRMATION}
      </Alert>
    );
  }

  if (!expanded) {
    return (
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Link
          component="button"
          type="button"
          onClick={() => setExpanded(true)}
          sx={{ color: 'primary.main', fontSize: '0.875rem', fontWeight: 500 }}
        >
          {linkLabel}
        </Link>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: 'left' }}>
        Enter the email address you registered with and we'll send a new link.
      </Typography>
      <TextField
        fullWidth
        size="small"
        label="Email Address"
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          if (emailError) setEmailError('');
        }}
        error={!!emailError}
        helperText={emailError}
        disabled={sending}
        sx={{ mb: 1.5 }}
        slotProps={{ htmlInput: { autoComplete: 'email' } }}
      />
      <Button type="submit" fullWidth variant="outlined" disabled={sending}>
        {sending ? 'Sending…' : 'Send verification link'}
      </Button>
    </Box>
  );
};

export default ResendVerification;
